import React from "react";

import {
  LucideSparkles,
  LucideX,
  LucideLightbulb,
  LucideHelpCircle,
  LucideHeart,
  LucideBookOpen,
  LucidePuzzle,
} from "lucide-react";

interface AIAssistantSuggestion {
  id: string;
  type: "clarification" | "example" | "encouragement" | "concept" | "problem";
  message: string;
  timestamp: Date;
  context?: string;
}

interface Props {
  suggestions: AIAssistantSuggestion[];
  isAnalyzing: boolean;
  onDismiss: (id: string) => void;
}

const AIAssistant: React.FC<Props> = ({ suggestions, isAnalyzing, onDismiss }) => {
  const getIcon = (type: string) => {
    switch (type) {
      case "clarification":
        return <LucideHelpCircle className="w-5 h-5" />;
      case "example":
        return <LucideBookOpen className="w-5 h-5" />;
      case "encouragement":
        return <LucideHeart className="w-5 h-5" />;
      case "concept":
        return <LucideLightbulb className="w-5 h-5" />;
      case "problem":
        return <LucidePuzzle className="w-5 h-5" />;
      default:
        return <LucideSparkles className="w-5 h-5" />;
    }
  };

  const getColor = (type: string) => {
    switch (type) {
      case "clarification":
        return "bg-blue-500";
      case "example":
        return "bg-purple-500";
      case "encouragement":
        return "bg-green-500";
      case "concept":
        return "bg-yellow-500";
      case "problem":
        return "bg-indigo-500";
      default:
        return "bg-gray-500";
    }
  };

  return (
    <div className="fixed bottom-20 right-4 z-50 flex flex-col gap-2 max-w-sm">
      {/* Analyzing indicator */}
      {isAnalyzing && (
        <div className="bg-gray-800 bg-opacity-90 backdrop-blur-sm rounded-lg p-2 px-3 flex items-center gap-2 self-end transition-all duration-300">
          <LucideSparkles className="w-4 h-4 text-white animate-pulse" />
          <span className="text-xs text-white">Luna is thinking...</span>
        </div>
      )}

      {/* Suggestions */}
      {suggestions.map((suggestion) => (
        <div
          key={suggestion.id}
          className="bg-white shadow-lg rounded-lg overflow-hidden transition-all duration-300 transform hover:scale-105"
        >
          <div className={`h-1 ${getColor(suggestion.type)}`} />
          <div className="p-4">
            <div className="flex items-start gap-3">
              <div className={`p-2 rounded-lg ${getColor(suggestion.type)} bg-opacity-10`}>
                <div className={`${getColor(suggestion.type).replace('bg-', 'text-')}`}>
                  {getIcon(suggestion.type)}
                </div>
              </div>
              <div className="flex-1">
                <div className="flex items-center justify-between mb-1">
                  <span className="text-xs font-medium text-gray-500 uppercase">
                    Luna AI - {suggestion.type}
                  </span>
                  <button
                    onClick={() => onDismiss(suggestion.id)}
                    className="text-gray-400 hover:text-gray-600 transition-colors"
                  >
                    <LucideX className="w-4 h-4" />
                  </button>
                </div>
                <p className="text-sm text-gray-800 leading-relaxed">
                  {suggestion.message}
                </p>
                {suggestion.context && (
                  <p className="text-xs text-gray-500 mt-2 italic">
                    Context: {suggestion.context}
                  </p>
                )}
              </div>
            </div>
          </div>
        </div>
      ))}
    </div>
  );
};

export default AIAssistant; 